import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Camera,
  Check,
  ChevronLeft,
  ChevronRight,
  Mail,
  MessageSquare,
  Phone,
  Play,
  Send,
  User,
  X,
} from "lucide-react";
import {
  AnimatePresence,
  motion,
  useDragControls,
  useReducedMotion,
  useScroll,
  useTransform,
} from "motion/react";
import "./ruteando.styles.css";
import { rsdContent } from "./ruteando.content";
import logoRuteando from "./assets/logo-ruteando-oficial-crop.webp";
import airaloLogo from "./assets/brand-airalo.webp";
import heroPhoto from "./assets/cabezal-horizontal.webp";
import badgeCommunity from "./assets/metric-community.webp";
import badgeViews from "./assets/metric-views.webp";
import badgeCountries from "./assets/metric-countries.webp";
import waysRouteBand from "./assets/ways-route-band.webp";
import storyCamperScene from "./assets/story-camper-scene.webp";
import stampUruguayAlaska from "./assets/stamp-uruguay-alaska.webp";
import tagAlaska from "./assets/tag-alaska.webp";
import tagUruguay from "./assets/tag-uruguay.webp";
import tagMarcas from "./assets/tag-marcas.webp";
import tagColabs from "./assets/tag-colabs.webp";

import brandHospedajes from "./assets/brand-hospedajes.webp";
import brandCampings from "./assets/brand-campings.webp";
import brandExperiencias from "./assets/brand-experiencias.webp";
import brandOutdoor from "./assets/brand-outdoor.webp";
import brandProductos from "./assets/brand-productos.webp";
import brandGastronomia from "./assets/brand-gastronomia.webp";
import brandDestinos from "./assets/brand-destinos.webp";
import ctaBgCartoon from "./assets/cta-bg-cartoon.webp";
import mapaRutaCover from "../mapa/assets/leccion-el-mapa-de-90-dias-vertical.webp";
import footerEmail from "./assets/footer-email.webp";
import footerWhatsapp from "./assets/footer-whatsapp.webp";
import footerInstagram from "./assets/footer-instagram.webp";
import footerFacebook from "./assets/footer-facebook.webp";
import footerYoutube from "./assets/footer-youtube.webp";
import footerTiktok from "./assets/footer-tiktok.webp";

import thumbIg01 from "./assets/portfolio/ig-01.webp";
import thumbIg02 from "./assets/portfolio/ig-02.webp";
import thumbIg03 from "./assets/portfolio/ig-03.webp";
import thumbIg04 from "./assets/portfolio/ig-04.webp";
import thumbIg05 from "./assets/portfolio/ig-05.webp";
import thumbIg06 from "./assets/portfolio/ig-06.webp";
import thumbIg07 from "./assets/portfolio/ig-07.webp";
import thumbIg08 from "./assets/portfolio/ig-08.webp";
import thumbIg09 from "./assets/portfolio/ig-09.jpg";
import thumbIg10 from "./assets/portfolio/ig-10.jpg";
import thumbTt01 from "./assets/portfolio/tt-01.webp";
import thumbTt02 from "./assets/portfolio/tt-02.webp";
import thumbTt03 from "./assets/portfolio/tt-03.webp";
import thumbTt04 from "./assets/portfolio/tt-04.webp";
import thumbTt05 from "./assets/portfolio/tt-05.webp";
import thumbTt06 from "./assets/portfolio/tt-06.webp";

const easeOut = [0.22, 1, 0.36, 1] as const;
const FOOTER_ICONS = {
  email: new URL(footerEmail, import.meta.url).href,
  whatsapp: new URL(footerWhatsapp, import.meta.url).href,
  instagram: new URL(footerInstagram, import.meta.url).href,
  facebook: new URL(footerFacebook, import.meta.url).href,
  youtube: new URL(footerYoutube, import.meta.url).href,
  tiktok: new URL(footerTiktok, import.meta.url).href,
};

// Contacto + redes (mismos iconos del footer); se reusan en la historia y en el footer.
const SOCIAL_LINKS = [
  { key: "email", href: `mailto:${rsdContent.links.email}`, ext: false, aria: "Escribinos por email", tip: rsdContent.links.email },
  { key: "whatsapp", href: rsdContent.links.whatsapp, ext: true, aria: "Escribinos por WhatsApp", tip: rsdContent.links.whatsappLabel },
  { key: "instagram", href: rsdContent.links.instagram, ext: true, aria: "Instagram", tip: "@ruteandosindestino" },
  { key: "facebook", href: rsdContent.links.facebook, ext: true, aria: "Facebook", tip: "Ruteando Sin Destino" },
  { key: "youtube", href: rsdContent.links.youtube, ext: true, aria: "YouTube", tip: "@ruteandosindestino" },
  { key: "tiktok", href: rsdContent.links.tiktok, ext: true, aria: "TikTok", tip: "@ruteando.sin.destino" },
] as const;

// Navegacion compartida (header desktop + menu full-screen mobile).
const NAV_LINKS = [
  { href: "#nosotros", label: "Nosotros", hint: "Quiénes somos y nuestra historia" },
  { href: "#marcas", label: "Marcas", hint: "Para qué marcas tiene sentido" },
  { href: "#portfolio", label: "Portfolio", hint: "Trabajos que ya publicamos" },
  { href: "#hablemos", label: "Hablemos", hint: "Proponé una colaboración", cta: true },
] as const;
const ctaBgSrc = new URL(ctaBgCartoon, import.meta.url).href;
// Este asset también pertenece al bundle de /mapa y esbuild lo emite desde un
// chunk compartido como `../archivo.webp`. La landing corre un nivel más arriba,
// por eso normalizamos la ruta para resolverla dentro de `/assets/`.
const mapaRutaCoverSrc = new URL(mapaRutaCover.replace(/^\.\.\//, "./"), import.meta.url).href;
const waysRouteBandSrc = new URL(waysRouteBand, import.meta.url).href;
const storyCamperSceneSrc = new URL(storyCamperScene, import.meta.url).href;
const stampUruguayAlaskaSrc = new URL(stampUruguayAlaska, import.meta.url).href;
const tagAlaskaSrc = new URL(tagAlaska, import.meta.url).href;
const tagUruguaySrc = new URL(tagUruguay, import.meta.url).href;
const tagMarcasSrc = new URL(tagMarcas, import.meta.url).href;
const tagColabsSrc = new URL(tagColabs, import.meta.url).href;

const BRAND_IMAGES: Record<string, string> = {
  hospedajes: new URL(brandHospedajes, import.meta.url).href,
  campings: new URL(brandCampings, import.meta.url).href,
  experiencias: new URL(brandExperiencias, import.meta.url).href,
  outdoor: new URL(brandOutdoor, import.meta.url).href,
  productos: new URL(brandProductos, import.meta.url).href,
  gastronomia: new URL(brandGastronomia, import.meta.url).href,
  destinos: new URL(brandDestinos, import.meta.url).href,
};
const PORTFOLIO_IMAGES: Record<string, string> = {
  "ig-01": new URL(thumbIg01, import.meta.url).href,
  "ig-02": new URL(thumbIg02, import.meta.url).href,
  "ig-03": new URL(thumbIg03, import.meta.url).href,
  "ig-04": new URL(thumbIg04, import.meta.url).href,
  "ig-05": new URL(thumbIg05, import.meta.url).href,
  "ig-06": new URL(thumbIg06, import.meta.url).href,
  "ig-07": new URL(thumbIg07, import.meta.url).href,
  "ig-08": new URL(thumbIg08, import.meta.url).href,
  "ig-09": new URL(thumbIg09, import.meta.url).href,
  "ig-10": new URL(thumbIg10, import.meta.url).href,
  "tt-01": new URL(thumbTt01, import.meta.url).href,
  "tt-02": new URL(thumbTt02, import.meta.url).href,
  "tt-03": new URL(thumbTt03, import.meta.url).href,
  "tt-04": new URL(thumbTt04, import.meta.url).href,
  "tt-05": new URL(thumbTt05, import.meta.url).href,
  "tt-06": new URL(thumbTt06, import.meta.url).href,
};
// Logo de marca sobre la tile cuando la pieza fue una colaboración pagada.
const PORTFOLIO_LOGOS: Record<string, string> = {
  "ig-01": new URL(airaloLogo, import.meta.url).href,
};

// Parte el título en principal (marca/lugar), detalle (lugar/rubro) y handle
// (@usuario del cliente), para darle a cada uno su propio diseño en la tile.
function splitPortfolioTitle(brand: string): {
  main: string;
  detail: string;
  handle: string;
} {
  const parts = brand
    .split(/\s+[|\-]\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const main = parts[0] ?? brand.trim();
  let handle = "";
  const details: string[] = [];
  for (const p of parts.slice(1)) {
    if (p.startsWith("@")) handle = p;
    else details.push(p);
  }
  return { main, detail: details.join(" · "), handle };
}
const logoRuteandoSrc = new URL(logoRuteando, import.meta.url).href;
const heroPhotoSrc = new URL(heroPhoto, import.meta.url).href;

function RsdReveal({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      className={className}
      initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 26 }}
      transition={{ delay: reduceMotion ? 0 : delay, duration: 0.72, ease: easeOut }}
      viewport={{ once: true, margin: "-80px" }}
      whileInView={{ opacity: 1, y: 0 }}
    >
      {children}
    </motion.div>
  );
}

function RsdButton({
  children,
  href,
  variant = "primary",
}: {
  children: React.ReactNode;
  href: string;
  variant?: "primary" | "secondary";
}) {
  const external = href.startsWith("http");

  return (
    <a
      className={`rsd-button rsd-button-${variant}`}
      href={href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {children}
    </a>
  );
}

function SectionIntro({
  eyebrow,
  eyebrowImg,
  title,
  text,
}: {
  eyebrow: string;
  eyebrowImg?: string;
  title: React.ReactNode;
  text?: string;
}) {
  return (
    <RsdReveal className="rsd-section-intro">
      {eyebrowImg ? (
        <img className="rsd-tag-label" src={eyebrowImg} alt={eyebrow} loading="lazy" />
      ) : (
        <span>{eyebrow}</span>
      )}
      <h2>{title}</h2>
      {text ? <p>{text}</p> : null}
    </RsdReveal>
  );
}

const METRIC_BADGES: Record<string, string> = {
  community: new URL(badgeCommunity, import.meta.url).href,
  views: new URL(badgeViews, import.meta.url).href,
  countries: new URL(badgeCountries, import.meta.url).href,
};

// Cuenta desde 0 hasta el valor real cuando la metrica entra en pantalla.
function RsdMetricValue({ value }: { value: string }) {
  const ref = useRef<HTMLElement>(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const match = value.match(/^([\d.,]+)(.*)$/);
    if (!match) {
      el.textContent = value;
      return;
    }
    const numStr = match[1].replace(",", ".");
    const target = parseFloat(numStr);
    const suffix = match[2];
    const decimals = numStr.includes(".") ? numStr.split(".")[1].length : 0;
    const format = (n: number) => `${n.toFixed(decimals)}${suffix}`;

    if (reduceMotion || typeof IntersectionObserver === "undefined") {
      el.textContent = value;
      return;
    }

    el.textContent = format(0);
    let raf = 0;
    let started = false;

    const animateUp = () => {
      const duration = 1500;
      const startTime = performance.now();
      const ease = (t: number) => 1 - Math.pow(1 - t, 3);
      const tick = (now: number) => {
        const p = Math.min(1, (now - startTime) / duration);
        if (p < 1) {
          el.textContent = format(target * ease(p));
          raf = requestAnimationFrame(tick);
        } else {
          el.textContent = value;
        }
      };
      raf = requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            started = true;
            animateUp();
            io.disconnect();
          }
        });
      },
      { threshold: 0.5 },
    );
    io.observe(el);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, [value, reduceMotion]);

  return (
    <strong className="rsd-milestone-value" ref={ref}>
      {value}
    </strong>
  );
}

// Hero: layout de dos columnas en desktop (copy | foto a sangre) + SVG de ruta en la base
// y sello "Rumbo a Alaska". En mobile, copy va primero y la foto queda debajo.
function RsdHero() {
  const heroRef = useRef<HTMLElement>(null);
  const reduceMotion = useReducedMotion();
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 981px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const photoY = useTransform(scrollYProgress, [0, 1], ["-7%", "9%"]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const stampRotate = useTransform(scrollYProgress, [0, 0.4, 1], [-4, 6, 18]);
  const stampScale = useTransform(scrollYProgress, [0, 0.4, 1], [1, 1.35, 1.8]);
  const stampY = useTransform(scrollYProgress, [0, 1], ["0%", "-60%"]);
  const stampOpacity = useTransform(scrollYProgress, [0, 0.7, 1], [1, 0.85, 0]);
  const parallax = isDesktop && !reduceMotion;

  return (
    <section className="rsd-hero" aria-labelledby="rsd-hero-title" ref={heroRef}>
      <motion.div
        className="rsd-hero-bg"
        aria-hidden="true"
        style={parallax ? { y: bgY } : undefined}
      />
      <div className="rsd-hero-copy">
          <motion.div
            className="rsd-hero-stamp"
            initial={{ opacity: 0, scale: 0.85, rotate: -12 }}
            animate={{ opacity: 1, scale: 1, rotate: -4 }}
            transition={{ delay: 0.3, duration: 0.7, ease: easeOut }}
            style={
              parallax
                ? {
                    rotate: stampRotate,
                    scale: stampScale,
                    y: stampY,
                    opacity: stampOpacity,
                  }
                : undefined
            }
          >
            <img
              src={stampUruguayAlaskaSrc}
              alt="Sello de viaje: De Uruguay a Alaska, sin escalas"
            />
          </motion.div>
          <motion.h1
            id="rsd-hero-title"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08, duration: 0.78, ease: easeOut }}
          >
            <span className="rsd-hero-title-2">
              Vida <span className="rsd-hero-hl rsd-hero-hl-2">nómade</span>
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.16, duration: 0.78, ease: easeOut }}
          >
            Somos <strong>Diego y Meli</strong>, dos uruguayos.
            <br />
            Vivimos viajando en nuestra <strong>casa con ruedas</strong> rumbo a{" "}
            <span className="rsd-emph-real">Alaska</span>.
            <br />
            Compartimos el día a día de una <strong>vida nómade real</strong>:
            rutas, fronteras, lugares increíbles, problemas y aprendizajes.
          </motion.p>
          <motion.div
            className="rsd-actions"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.24, duration: 0.7, ease: easeOut }}
          >
            <RsdButton href={rsdContent.links.mediaKit}>
              Ver Media Kit <ChevronRight className="rsd-btn-icon-arrow" size={18} />
            </RsdButton>
            <RsdButton href={rsdContent.links.collaboration} variant="secondary">
              Colaboremos <ArrowRight className="rsd-btn-icon-arrow" size={18} />
            </RsdButton>
          </motion.div>
      </div>

      <motion.div
        className="rsd-hero-visual"
        initial={{ opacity: 0, scale: 0.97, y: 18 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ delay: 0.14, duration: 0.82, ease: easeOut }}
      >
        <div className="rsd-photo-space">
          <motion.img
            src={heroPhotoSrc}
            alt="Melisa y Diego junto al motorhome en la ruta"
            style={parallax ? { y: photoY } : undefined}
          />
        </div>
      </motion.div>
    </section>
  );
}

// Fila sutil de iconos de contacto/redes (reusa los stickers del footer).
function RsdStorySocial() {
  return (
    <div className="rsd-story-social">
      <span className="rsd-story-social-label">Seguinos</span>
      <div className="rsd-story-social-icons">
        {SOCIAL_LINKS.map((item) => (
          <a
            key={item.key}
            className="rsd-story-social-icon"
            href={item.href}
            aria-label={item.aria}
            {...(item.ext ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          >
            <img src={FOOTER_ICONS[item.key]} alt="" loading="lazy" />
          </a>
        ))}
      </div>
    </div>
  );
}

// Historia: el texto vive centrado en el modulo y hace un parallax sutil al hacer scroll (solo desktop).
function RsdStorySection() {
  const sectionRef = useRef<HTMLElement>(null);
  const reduceMotion = useReducedMotion();
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 981px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [72, -72]);
  const parallax = isDesktop && !reduceMotion;

  return (
    <section className="rsd-section rsd-story" id="nosotros" ref={sectionRef}>
      <RsdReveal className="rsd-story-grid">
        <div className="rsd-story-head">
          <img
            className="rsd-story-flag-tag"
            src={tagUruguaySrc}
            alt="Dos uruguayos"
            loading="lazy"
          />
          <h2>
            <span className="rsd-story-title-top">Un CAMPER</span>
            <span className="rsd-story-title-bottom">
              <span className="rsd-emph rsd-emph-real">SUEÑOS</span> por cumplir.
            </span>
          </h2>
          <div className="rsd-story-figure-wrap">
            <img
              className="rsd-story-figure"
              src={storyCamperSceneSrc}
              alt="Ilustración de nuestro motorhome en un mirador de montaña"
              loading="lazy"
            />
            <img
              className="rsd-story-stamp"
              src={tagAlaskaSrc}
              alt="Camino a Alaska"
              loading="lazy"
            />
          </div>
        </div>
        <motion.div className="rsd-story-copy" style={parallax ? { y } : undefined}>
          <RsdStorySocial />
          <p className="rsd-story-lead">
            Somos dos uruguayos que un día decidimos{" "}
            <strong>cambiar la forma de vivir</strong>. Vendimos nuestras cosas, dejamos atrás
            lo conocido y salimos a buscar una vida{" "}
            <strong>más simple, más libre y más conectada con la naturaleza</strong>.
          </p>
          <p>
            Desde el <strong>14 de noviembre de 2025</strong> vivimos viajando en motorhome,
            aunque nuestro amor por la ruta empezó mucho antes: ya llevábamos{" "}
            <strong>más de dos años recorriendo</strong>, descubriendo lugares y entendiendo que
            viajar no era solo una escapada, <strong>sino la forma de vida que queríamos
            construir</strong>.
          </p>
          <p>
            Hoy compartimos ese camino con una{" "}
            <strong>comunidad que nos acompaña día a día</strong>. Mostramos paisajes, comidas,
            experiencias, productos que usamos, lugares que visitamos y{" "}
            <strong>momentos reales de la vida en ruta</strong>.
          </p>
        </motion.div>
      </RsdReveal>
    </section>
  );
}

const MAPA_RUTA_BENEFITS = [
  "Descubrí si la vida nómada realmente es para vos.",
  "Organizá tu casa sobre ruedas.",
  "Prepará tus ingresos y tu presupuesto.",
  "Resolvé conectividad, salud, papeles y fronteras.",
  "Convertí la idea en un plan para empezar.",
] as const;

function RsdMapaRutaSection() {
  return (
    <section className="rsd-section rsd-mapa-ruta" aria-labelledby="rsd-mapa-ruta-title">
      <div className="rsd-mapa-ruta-grid">
        <RsdReveal className="rsd-mapa-ruta-copy">
          <span className="rsd-kicker rsd-mapa-ruta-eyebrow">
            GUÍA GRATUITA · MAPA DE RUTA
          </span>
          <h2 id="rsd-mapa-ruta-title">
            <span className="rsd-mapa-ruta-title-line">Del “algún día” a tu{" "}</span>
            <span className="rsd-mapa-ruta-title-line rsd-mapa-ruta-title-emphasis">
              primer kilómetro
            </span>
          </h2>
          <p className="rsd-mapa-ruta-lead">
            Armamos la guía que nos hubiera gustado tener antes de salir: un recorrido
            práctico para transformar las ganas de vivir viajando en decisiones concretas.
          </p>

          <ul className="rsd-mapa-ruta-benefits" aria-label="Qué vas a encontrar en la guía">
            {MAPA_RUTA_BENEFITS.map((benefit) => (
              <li key={benefit}>
                <span className="rsd-mapa-ruta-check" aria-hidden="true">
                  <Check size={14} strokeWidth={2.6} />
                </span>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>

          <div className="rsd-mapa-ruta-action">
            <a className="rsd-mapa-ruta-button" href="/mapa">
              Quiero empezar mi ruta <span aria-hidden="true">→</span>
            </a>
            <p className="rsd-mapa-ruta-microcopy">
              <span>Acceso gratuito e inmediato. Guardá</span>
              <span>tu avance y continuá cuando quieras.</span>
            </p>
          </div>
        </RsdReveal>

        <RsdReveal className="rsd-mapa-ruta-visual" delay={0.12}>
          <div className="rsd-mapa-ruta-cover">
            <img
              src={mapaRutaCoverSrc}
              alt="Ilustración de la guía Mapa de Ruta con un plan de viaje, un motorhome y un mate"
              loading="lazy"
            />
          </div>
        </RsdReveal>
      </div>
    </section>
  );
}

// reCAPTCHA v3: la SITE KEY es pública por diseño (va en el cliente, se inyecta
// en el build con VITE_RECAPTCHA_SITE_KEY). La clave secreta vive solo en el
// servidor (variable de entorno).
const RECAPTCHA_SITE_KEY: string = import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? "";

// Carga el script de reCAPTCHA una sola vez y devuelve una promesa que resuelve
// cuando grecaptcha está listo.
let recaptchaPromise: Promise<void> | null = null;
function loadRecaptcha(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (recaptchaPromise) return recaptchaPromise;
  recaptchaPromise = new Promise<void>((res, rej) => {
    if ((window as any).grecaptcha) return res();
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    s.async = true;
    s.defer = true;
    s.onload = () => res();
    s.onerror = () => rej(new Error("recaptcha-load-failed"));
    document.head.appendChild(s);
  });
  return recaptchaPromise;
}

// Ejecuta reCAPTCHA v3 para una acción y devuelve el token (o "" si falla).
async function getRecaptchaToken(action: string): Promise<string> {
  if (!RECAPTCHA_SITE_KEY) return "";
  try {
    await loadRecaptcha();
    const grecaptcha = (window as any).grecaptcha;
    await new Promise<void>((r) => grecaptcha.ready(r));
    return await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
  } catch {
    return "";
  }
}

// Hook compartido para los formularios (cada variante visual usa el mismo estado).
// `servicio` guarda el tipo elegido en el selector del wizard; el server ya lo
// acepta y lo incluye en el mail. `marca` queda disponible por si se usa.
function useContactForm() {
  const empty = { nombre: "", email: "", whatsapp: "", marca: "", servicio: "", mensaje: "" };
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "error">("idle");

  // Precargamos el script de reCAPTCHA apenas se monta el formulario, así el
  // token está listo al enviar (sin demora perceptible).
  useEffect(() => {
    loadRecaptcha().catch(() => {});
  }, []);

  const clearError = (key: string) => {
    if (errors[key]) setErrors((er) => ({ ...er, [key]: "" }));
  };

  const update =
    (key: keyof typeof empty) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((f) => ({ ...f, [key]: e.target.value }));
      clearError(key);
    };

  // Setter directo (para el selector de chips del wizard).
  const setField = (key: keyof typeof empty, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    clearError(key);
  };

  const validate = () => {
    const er: Record<string, string> = {};
    if (!form.servicio.trim()) er.servicio = "Elegí una opción";
    if (!form.nombre.trim()) er.nombre = "Falta tu nombre";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) er.email = "Falta un email válido";
    if (!form.mensaje.trim()) er.mensaje = "Escribinos algo aunque sea breve";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === "sending") return;
    if (!validate()) return;
    setStatus("sending");
    try {
      const recaptchaToken = await getRecaptchaToken("contact");
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, recaptchaToken }),
      });
      if (res.ok) {
        setStatus("ok");
        setForm(empty);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return { form, errors, status, update, setField, setErrors, handleSubmit };
}

/* ──────────────────────────────────────────────────────────────────────────
   SECCIÓN COMBINADA · CTA Colaboraciones + Bitácora con formulario
   Imagen de fondo (camper + ruta) con parallax + título del CTA de marca +
   libreta de bitácora con el formulario de contacto dentro. Una sola sección
   que hace de cierre: mensaje de marca + acción de contacto en el mismo lugar.
   ────────────────────────────────────────────────────────────────────────── */
// Tipos de propuesta para el primer paso del wizard (selector con foto de marca).
// `value` es lo que viaja al mail; `title`/`hint` son solo para mostrar.
const PROPOSAL_TYPES = [
  {
    value: "Hospedaje",
    title: "Un hospedaje",
    hint: "Cabañas, hoteles, glamping o camping",
    icon: BRAND_IMAGES.hospedajes,
  },
  {
    value: "Destino",
    title: "Un destino",
    hint: "Un pueblo o lugar para descubrir",
    icon: BRAND_IMAGES.destinos,
  },
  {
    value: "Experiencia",
    title: "Una experiencia",
    hint: "Tours, actividades y salidas",
    icon: BRAND_IMAGES.experiencias,
  },
  {
    value: "Producto",
    title: "Un producto",
    hint: "Algo para usar y probar en ruta",
    icon: BRAND_IMAGES.productos,
  },
] as const;

const WIZARD_STEPS = ["Propuesta", "Tus datos", "Tu idea"] as const;

function RsdContactCta() {
  const { form, errors, status, update, setField, setErrors, handleSubmit } = useContactForm();
  const [step, setStep] = useState(0);
  const reduceMotion = useReducedMotion();
  const emailRe = /^\S+@\S+\.\S+$/;

  const selectedType = PROPOSAL_TYPES.find((t) => t.value === form.servicio);

  // Parallax de la banda: entra desde abajo y el camper se acerca al hacer scroll.
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const bandY = useTransform(scrollYProgress, [0, 0.7], [170, 0]);
  const bandScale = useTransform(scrollYProgress, [0, 1], [0.86, 1.26]);

  // Animación de alto del wizard: medimos el panel activo y animamos el alto del
  // contenedor. Al ser un alto real (no transform), el fondo/banda de abajo se
  // reacomoda solo, en flujo, siguiendo el cambio de forma.
  const stageRef = useRef<HTMLDivElement>(null);
  const [stageH, setStageH] = useState<number | "auto">("auto");
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => setStageH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Al elegir el tipo de propuesta avanzamos solos al paso siguiente; el pequeño
  // delay deja ver el estado "seleccionado" antes de la transición.
  const selectType = (value: string) => {
    setField("servicio", value);
    window.setTimeout(() => setStep(1), 240);
  };
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const goToIdea = () => {
    const er: Record<string, string> = {};
    if (!form.nombre.trim()) er.nombre = "Falta tu nombre";
    if (!emailRe.test(form.email.trim())) er.email = "Falta un email válido";
    if (Object.keys(er).length) {
      setErrors(er);
      return;
    }
    setStep(2);
  };

  const onEnterAdvance = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      goToIdea();
    }
  };

  // Transición entre pasos (respeta reduce-motion).
  const panelMotion = reduceMotion
    ? { initial: false as const, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.12 } }
    : {
        initial: { opacity: 0, x: 18 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: -18 },
        transition: { duration: 0.26, ease: easeOut },
      };

  const typePill = selectedType ? (
    <button type="button" className="rsd-wizard-typepill" onClick={() => setStep(0)}>
      <img src={selectedType.icon} alt="" aria-hidden="true" />
      <span>
        Proponés · <strong>{selectedType.value}</strong>
      </span>
      <span className="rsd-wizard-typepill-edit">Cambiar</span>
    </button>
  ) : null;

  return (
    <section className="rsd-contact-cta" id="hablemos" ref={sectionRef}>
      <div className="rsd-contact-cta-inner">
        <RsdReveal className="rsd-contact-cta-head">
          <span className="rsd-kicker">Colaboraciones</span>
          <h2>
            ¿Tu marca quiere formar parte de una historia en{" "}
            <span className="rsd-emph rsd-emph-real">movimiento</span>?
          </h2>
          <p>
            Si tenés un <strong>hospedaje, destino, experiencia o producto</strong> que
            conecta con la vida en ruta, <strong>creamos algo juntos</strong>.
          </p>

          <div className="rsd-contact-direct">
            <span className="rsd-contact-direct-label">¿Preferís algo más directo?</span>
            <div className="rsd-contact-direct-actions">
              <a
                className="rsd-contact-chip rsd-contact-chip--wa"
                href={rsdContent.links.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={FOOTER_ICONS.whatsapp} alt="" aria-hidden="true" />
                WhatsApp
              </a>
              <a className="rsd-contact-chip" href={`mailto:${rsdContent.links.email}`}>
                <img src={FOOTER_ICONS.email} alt="" aria-hidden="true" />
                Email
              </a>
              <a
                className="rsd-contact-chip"
                href={rsdContent.links.instagram}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={FOOTER_ICONS.instagram} alt="" aria-hidden="true" />
                Instagram
              </a>
            </div>
          </div>
        </RsdReveal>

        <RsdReveal className="rsd-bitacora rsd-bitacora--in-cta" delay={0.08}>
          <div className="rsd-bitacora-spine" aria-hidden="true" />

          <div className="rsd-bitacora-head">
            <img className="rsd-bitacora-stamp" src={tagAlaskaSrc} alt="" aria-hidden="true" loading="lazy" />
            <div>
              <span className="rsd-bitacora-eyebrow">Bitácora · entrada nueva</span>
              <h3 className="rsd-bitacora-title">Anotanos tu propuesta</h3>
            </div>
          </div>

          {status === "ok" ? (
            <div className="rsd-wizard-done" role="status">
              <span className="rsd-wizard-done-check" aria-hidden="true">
                <Check size={26} />
              </span>
              <h4>¡Anotado en la bitácora!</h4>
              <p>Recibimos tu propuesta. Te respondemos desde la próxima parada.</p>
            </div>
          ) : (
            <div className="rsd-bitacora-body">
              {/* Progreso del wizard */}
              <div className="rsd-wizard-steps">
                {WIZARD_STEPS.map((label, i) => (
                  <button
                    key={label}
                    type="button"
                    className={`rsd-wizard-step${i === step ? " is-active" : ""}${
                      i < step ? " is-done" : ""
                    }`}
                    onClick={() => (i < step ? setStep(i) : undefined)}
                    disabled={i >= step}
                  >
                    <span className="rsd-wizard-step-dot">
                      {i < step ? <Check size={14} /> : i + 1}
                    </span>
                    <span className="rsd-wizard-step-label">{label}</span>
                  </button>
                ))}
              </div>

              <form className="rsd-wizard-form" onSubmit={handleSubmit} noValidate>
                <motion.div
                  className="rsd-wizard-stage"
                  animate={{ height: stageH }}
                  transition={reduceMotion ? { duration: 0 } : { duration: 0.36, ease: easeOut }}
                >
                  <div className="rsd-wizard-stage-inner" ref={stageRef}>
                    <AnimatePresence mode="popLayout" initial={false}>
                    {/* Paso 1 · Selector de tipo de propuesta */}
                    {step === 0 ? (
                      <motion.div key="step-0" className="rsd-wizard-panel" {...panelMotion}>
                        <p className="rsd-wizard-q">¿Qué querés proponer?</p>
                        <p className="rsd-wizard-help">Tocá una opción para elegirla.</p>
                        <div className="rsd-wizard-options" role="radiogroup" aria-label="Tipo de propuesta">
                          {PROPOSAL_TYPES.map((opt) => {
                            const active = form.servicio === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                className={`rsd-wizard-option${active ? " is-selected" : ""}`}
                                onClick={() => selectType(opt.value)}
                              >
                                <span className="rsd-wizard-option-media">
                                  <img src={opt.icon} alt="" aria-hidden="true" loading="lazy" />
                                  <span className="rsd-wizard-option-radio" aria-hidden="true">
                                    <Check size={14} />
                                  </span>
                                </span>
                                <span className="rsd-wizard-option-title">{opt.title}</span>
                                <span className="rsd-wizard-option-hint">{opt.hint}</span>
                              </button>
                            );
                          })}
                        </div>
                      </motion.div>
                    ) : null}

                    {/* Paso 2 · Datos de contacto */}
                    {step === 1 ? (
                      <motion.div key="step-1" className="rsd-wizard-panel" {...panelMotion}>
                        {typePill}
                        <p className="rsd-wizard-q">¿Quién nos escribe?</p>

                        <label className={`rsd-bitacora-field${errors.nombre ? " has-error" : ""}`}>
                          <span className="rsd-bitacora-field-label">Nombre o marca</span>
                          <span className="rsd-field-input">
                            <User className="rsd-field-icon" size={17} aria-hidden="true" />
                            <input
                              type="text"
                              name="nombre"
                              value={form.nombre}
                              onChange={update("nombre")}
                              onKeyDown={onEnterAdvance}
                              placeholder="Cómo te llamás"
                              autoComplete="name"
                            />
                          </span>
                          {errors.nombre ? <em className="rsd-field-error">{errors.nombre}</em> : null}
                        </label>

                        <label className={`rsd-bitacora-field${errors.email ? " has-error" : ""}`}>
                          <span className="rsd-bitacora-field-label">Email de contacto</span>
                          <span className="rsd-field-input">
                            <Mail className="rsd-field-icon" size={17} aria-hidden="true" />
                            <input
                              type="email"
                              name="email"
                              value={form.email}
                              onChange={update("email")}
                              onKeyDown={onEnterAdvance}
                              placeholder="tu@email.com"
                              autoComplete="email"
                            />
                          </span>
                          {errors.email ? <em className="rsd-field-error">{errors.email}</em> : null}
                        </label>

                        <label className="rsd-bitacora-field">
                          <span className="rsd-bitacora-field-label">
                            WhatsApp <i>(opcional)</i>
                          </span>
                          <span className="rsd-field-input">
                            <Phone className="rsd-field-icon" size={17} aria-hidden="true" />
                            <input
                              type="tel"
                              name="whatsapp"
                              value={form.whatsapp}
                              onChange={update("whatsapp")}
                              onKeyDown={onEnterAdvance}
                              placeholder="+598 92042015"
                              autoComplete="tel"
                            />
                          </span>
                        </label>

                        <div className="rsd-wizard-nav">
                          <button type="button" className="rsd-button rsd-button-secondary" onClick={goBack}>
                            <ChevronLeft size={18} />
                            Atrás
                          </button>
                          <button type="button" className="rsd-button rsd-button-primary" onClick={goToIdea}>
                            Siguiente
                            <ChevronRight size={18} />
                          </button>
                        </div>
                      </motion.div>
                    ) : null}

                    {/* Paso 3 · Mensaje + envío */}
                    {step === 2 ? (
                      <motion.div key="step-2" className="rsd-wizard-panel" {...panelMotion}>
                        {typePill}
                        <p className="rsd-wizard-q">Contanos tu idea</p>
                        <label className={`rsd-bitacora-field${errors.mensaje ? " has-error" : ""}`}>
                          <span className="rsd-bitacora-field-label">
                            <MessageSquare size={14} aria-hidden="true" /> Sobre tu{" "}
                            {form.servicio ? form.servicio.toLowerCase() : "propuesta"}
                          </span>
                          <textarea
                            name="mensaje"
                            rows={4}
                            value={form.mensaje}
                            onChange={update("mensaje")}
                            placeholder="Contanos qué tenés y qué te gustaría crear juntos."
                          />
                          {errors.mensaje ? <em className="rsd-field-error">{errors.mensaje}</em> : null}
                        </label>

                        <div className="rsd-wizard-nav">
                          <button type="button" className="rsd-button rsd-button-secondary" onClick={goBack}>
                            <ChevronLeft size={18} />
                            Atrás
                          </button>
                          <button
                            type="submit"
                            className="rsd-button rsd-button-primary rsd-form-submit"
                            disabled={status === "sending"}
                          >
                            {status === "sending" ? "Anotando..." : "Anotar en la bitácora"}
                            <Send className="rsd-btn-icon-arrow" size={18} />
                          </button>
                        </div>

                        {status === "error" ? (
                          <p className="rsd-form-note rsd-form-note--error" role="alert">
                            No se pudo enviar. Probá de nuevo o escribinos por WhatsApp.
                          </p>
                        ) : null}
                      </motion.div>
                    ) : null}
                    </AnimatePresence>
                  </div>
                </motion.div>
              </form>
            </div>
          )}
        </RsdReveal>
      </div>

      <motion.div
        className="rsd-contact-cta-band"
        aria-hidden="true"
        style={reduceMotion ? undefined : { y: bandY, scale: bandScale }}
      >
        <img src={waysRouteBandSrc} alt="" loading="lazy" />
      </motion.div>
    </section>
  );
}

// Portfolio: grid de miniaturas reales de Instagram y TikTok. Al clickear abre el lightbox.
function RsdPortfolio() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  // Random por load: que Instagram y TikTok aparezcan mezclados.
  // Se guarda el orden en ref para que el lightbox siga al item clickeado
  // aun si React re-renderiza por otro motivo.
  const itemsRef = useRef<(typeof rsdContent.portfolio)[number][] | null>(null);
  if (itemsRef.current === null) {
    const source = [...rsdContent.portfolio];
    for (let i = source.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [source[i], source[j]] = [source[j], source[i]];
    }
    itemsRef.current = source;
  }
  const items = itemsRef.current;

  // Cerrar y navegar son estables para que el efecto del lightbox
  // (listeners de teclado, focus trap) no se re-monte en cada render.
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const navigate = useCallback(
    (dir: number) =>
      setLightboxIndex((i) => (i === null ? i : (i + dir + items.length) % items.length)),
    [items.length],
  );

  return (
    <section className="rsd-section rsd-portfolio" id="portfolio">
      <div className="rsd-pf-head">
        <RsdReveal className="rsd-pf-intro">
          <img
            className="rsd-pf-colabs-sticker"
            src={tagColabsSrc}
            alt=""
            aria-hidden="true"
            loading="lazy"
          />
          <h2>
            Marcas que se sumaron al{" "}
            <span className="rsd-emph rsd-emph-real">VIAJE</span>.
          </h2>
          <p>
            Estas son algunas colaboraciones que formaron parte de nuestra ruta: experiencias,
            productos y momentos reales que compartimos con nuestra comunidad.
          </p>
        </RsdReveal>
      </div>

      <div className="rsd-pf-grid">
        {items.map((item, index) => {
          const title = splitPortfolioTitle(item.brand);
          return (
          <button
            key={item.thumb}
            type="button"
            className="rsd-pf-tile"
            onClick={() => setLightboxIndex(index)}
          >
            <div className="rsd-pf-media">
              <img
                src={PORTFOLIO_IMAGES[item.thumb] ?? BRAND_IMAGES[item.image]}
                alt={item.brand}
                loading="lazy"
              />
            </div>
            <span className="rsd-pf-badge">
              {item.platform === "tiktok" ? "TikTok" : "Instagram"}
            </span>
            {PORTFOLIO_LOGOS[item.thumb] ? (
              <span className="rsd-pf-logo" aria-hidden="true">
                <img src={PORTFOLIO_LOGOS[item.thumb]} alt="" loading="lazy" />
              </span>
            ) : null}
            <span className="rsd-pf-play" aria-hidden="true">
              <Play size={20} fill="currentColor" />
            </span>
            <div className="rsd-pf-info">
              <h3>
                <span className="rsd-pf-title-main">{title.main}</span>
                {title.detail ? (
                  <span className="rsd-pf-title-sub">{title.detail}</span>
                ) : null}
                {title.handle ? (
                  <span className="rsd-pf-handle">{title.handle}</span>
                ) : null}
              </h3>
              {item.caption ? <p>{item.caption}</p> : null}
            </div>
          </button>
          );
        })}
      </div>

      <AnimatePresence>
        {lightboxIndex !== null && (
          <RsdLightbox
            key="lightbox"
            item={items[lightboxIndex]}
            index={lightboxIndex}
            count={items.length}
            onClose={closeLightbox}
            onNavigate={navigate}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   LIGHTBOX · Modal fullscreen que reproduce el video nativo (.mp4 propio,
   servido desde el bucket S3 vía /api/video) con link al post original.
   ────────────────────────────────────────────────────────────────────────── */
function RsdLightbox({
  item,
  index,
  count,
  onClose,
  onNavigate,
}: {
  item: (typeof rsdContent.portfolio)[number];
  index: number;
  count: number;
  onClose: () => void;
  onNavigate: (dir: number) => void;
}) {
  const reduceMotion = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [videoUrl, setVideoUrl] = useState("");
  const platform = item.platform === "tiktok" ? "TikTok" : "Instagram";
  const embedUrl = "embedUrl" in item ? item.embedUrl : "";

  // Al cambiar de item pedimos al backend una URL prefirmada del video en el
  // bucket privado y mostramos el skeleton hasta que esté listo.
  useEffect(() => {
    setLoading(true);
    setVideoUrl("");
    if (embedUrl) return;
    let alive = true;
    fetch(`/api/video?id=${item.thumb}`)
      .then((r) => r.json())
      .then((d) => {
        if (alive && d?.url) setVideoUrl(d.url);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [embedUrl, item.thumb]);

  // Teclado (Esc cierra, flechas navegan, Tab queda atrapado) + focus inicial
  // + bloqueo del scroll de fondo. Deps estables => se monta una sola vez.
  useEffect(() => {
    // iOS y los in-app browsers de IG/TikTok ignoran overflow:hidden en body:
    // misma tecnica position:fixed + offset que usa el menu mobile.
    const scroller = document.scrollingElement || document.documentElement;
    const scrollY = scroller.scrollTop;
    const body = document.body;
    const prevStyles = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";

    const panel = panelRef.current;
    const focusables = panel
      ? Array.from(
          panel.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'),
        )
      : [];
    focusables[0]?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") return onClose();
      if (e.key === "ArrowLeft") return onNavigate(-1);
      if (e.key === "ArrowRight") return onNavigate(1);
      if (e.key === "Tab" && focusables.length) {
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || !panel?.contains(active as Node))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      body.style.position = prevStyles.position;
      body.style.top = prevStyles.top;
      body.style.width = prevStyles.width;
      body.style.overflow = prevStyles.overflow;
      scroller.scrollTop = scrollY;
    };
  }, [onClose, onNavigate]);

  return (
    <motion.div
      className="rsd-drawer"
      ref={panelRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: easeOut }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.aside
        className="rsd-drawer-panel"
        role="dialog"
        aria-modal="true"
        aria-label={item.caption ? `${item.brand} — ${item.caption}` : item.brand}
        initial={reduceMotion ? { opacity: 0 } : { x: "100%" }}
        animate={reduceMotion ? { opacity: 1 } : { x: 0 }}
        exit={reduceMotion ? { opacity: 0 } : { x: "100%" }}
        transition={
          reduceMotion
            ? { duration: 0.2 }
            : { type: "spring", stiffness: 340, damping: 36, mass: 0.9 }
        }
      >
        <header className="rsd-drawer-head">
          <div className="rsd-drawer-titles">
            <span className="rsd-drawer-eyebrow">Ruteando · {item.type}</span>
            <h3>{item.brand}</h3>
          </div>
          <span className="rsd-drawer-platform">{platform}</span>
          <button
            type="button"
            className="rsd-drawer-close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </header>

        <div className="rsd-drawer-frame">
          {loading && (
            <div className="rsd-drawer-loading" aria-hidden="true">
              <span className="rsd-drawer-spinner" />
              <span className="rsd-drawer-loading-label">Cargando video…</span>
            </div>
          )}
          {embedUrl ? (
            <iframe
              key={item.thumb}
              src={embedUrl}
              title={`${item.brand} en Instagram`}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              loading="eager"
              onLoad={() => setLoading(false)}
              style={{ opacity: loading ? 0 : 1 }}
            />
          ) : videoUrl ? (
            <video
              key={item.thumb}
              className="rsd-drawer-video"
              ref={(el) => {
                if (el) el.muted = true;
              }}
              src={videoUrl}
              poster={PORTFOLIO_IMAGES[item.thumb]}
              autoPlay
              muted
              loop
              playsInline
              controls
              preload="auto"
              onCanPlay={() => setLoading(false)}
              style={{ opacity: loading ? 0 : 1 }}
            />
          ) : null}
        </div>

        <footer className="rsd-drawer-foot">
          {item.caption ? <p className="rsd-drawer-caption">{item.caption}</p> : null}
          <div className="rsd-drawer-actions">
            <a
              className="rsd-drawer-link"
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ver en {platform}
              <ArrowUpRight size={16} />
            </a>
            {count > 1 && (
              <div className="rsd-drawer-nav">
                <button
                  type="button"
                  className="rsd-drawer-nav-btn"
                  onClick={() => onNavigate(-1)}
                  aria-label="Anterior"
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="rsd-drawer-counter">
                  {index + 1} / {count}
                </span>
                <button
                  type="button"
                  className="rsd-drawer-nav-btn"
                  onClick={() => onNavigate(1)}
                  aria-label="Siguiente"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </div>
        </footer>
      </motion.aside>
    </motion.div>
  );
}

// Icono inline de WhatsApp para el boton flotante (lucide ya no exporta glyphs de marca).
function IconWhatsapp({ size = 18 }: { size?: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.52 3.48A11.86 11.86 0 0 0 12.04.05C5.5.05.2 5.35.2 11.89c0 2.09.55 4.13 1.6 5.93L.1 23.95l6.31-1.65a11.85 11.85 0 0 0 5.63 1.43h.01c6.54 0 11.85-5.3 11.85-11.84 0-3.17-1.23-6.14-3.47-8.38ZM12.05 21.7c-1.78 0-3.52-.48-5.04-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.18c0-5.43 4.42-9.85 9.86-9.85 2.63 0 5.1 1.03 6.96 2.89a9.78 9.78 0 0 1 2.88 6.97c0 5.44-4.42 9.86-9.85 9.86Zm5.4-7.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.66.15-.2.3-.76.96-.93 1.16-.17.2-.34.22-.64.07-.3-.15-1.25-.46-2.39-1.47a8.96 8.96 0 0 1-1.65-2.06c-.17-.3-.02-.46.13-.6.13-.13.3-.34.45-.5.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.66-1.6-.91-2.18-.24-.57-.48-.5-.66-.5l-.56-.01c-.2 0-.5.07-.77.37-.27.3-1.02 1-1.02 2.44 0 1.43 1.05 2.82 1.2 3.02.15.2 2.07 3.16 5.01 4.43.7.3 1.25.48 1.67.62.7.22 1.34.19 1.85.11.56-.08 1.75-.71 1.99-1.4.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.34Z" />
    </svg>
  );
}

// Footer minimo: solo iconos (contacto + redes), cada uno con su tooltip de marca.
function RsdFooter() {
  return (
    <footer className="rsd-footer">
      <div className="rsd-footer-brand">
        <img src={logoRuteandoSrc} alt="Ruteando Sin Destino" />
        <p className="rsd-footer-manifesto">
          De Uruguay a Alaska. En motorhome. Desde 2025.
        </p>
      </div>
      <nav className="rsd-footer-icons" aria-label="Contacto y redes">
        {SOCIAL_LINKS.map((item) => (
          <a
            key={item.key}
            className={`rsd-footer-icon rsd-footer-icon--${item.key}`}
            href={item.href}
            aria-label={item.aria}
            {...(item.ext ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          >
            <img src={FOOTER_ICONS[item.key]} alt="" loading="lazy" />
            <span className="rsd-footer-tip" role="tooltip">
              {item.tip}
            </span>
          </a>
        ))}
      </nav>
      <div className="rsd-footer-bottom">
        <a className="rsd-footer-medakit" href={rsdContent.links.mediaKit}>
          Ver media kit <ArrowRight size={14} />
        </a>
        <p className="rsd-footer-copy">© 2026 Ruteando Sin Destino</p>
      </div>
    </footer>
  );
}

// Boton flotante de WhatsApp.
function RsdWhatsappFab() {
  return (
    <a
      className="rsd-wa-fab"
      href={rsdContent.links.whatsapp}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Escribinos por WhatsApp"
    >
      <IconWhatsapp size={28} />
      <span className="rsd-wa-fab-label">Escribinos</span>
    </a>
  );
}

// Menu full-screen para mobile: revelado en cortina, navegacion editorial y branding del cliente.
function RsdMobileMenu({
  open,
  onClose,
  onNavigate,
  activeId,
}: {
  open: boolean;
  onClose: () => void;
  onNavigate: () => void;
  activeId: string;
}) {
  const reduceMotion = useReducedMotion();
  const items = NAV_LINKS.filter((l) => !("cta" in l && l.cta));
  const cta = NAV_LINKS.find((l) => "cta" in l && l.cta) ?? NAV_LINKS[NAV_LINKS.length - 1];
  const panelRef = useRef<HTMLDivElement>(null);
  const dragControls = useDragControls();

  // Focus management: al abrir, enfoca el panel; al cerrar, devuelve el foco al toggle.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusables = panel.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    const first = focusables[0];
    if (first) first.focus();

    // Focus trap: Tab y Shift+Tab no salen del dialog.
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || focusables.length === 0) return;
      const active = document.activeElement as HTMLElement | null;
      const firstEl = focusables[0];
      const lastEl = focusables[focusables.length - 1];
      if (e.shiftKey && (active === firstEl || !panel.contains(active))) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && active === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    panel.addEventListener("keydown", onKeydown);
    return () => panel.removeEventListener("keydown", onKeydown);
  }, [open]);

  const panel = reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { clipPath: "inset(0 0 100% 0)", opacity: 0.4 },
        animate: { clipPath: "inset(0 0 0% 0)", opacity: 1 },
        exit: { clipPath: "inset(0 0 100% 0)", opacity: 0.2 },
      };

  const row = (i: number) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 26 },
          animate: { opacity: 1, y: 0 },
          transition: { delay: 0.18 + i * 0.085, duration: 0.52, ease: easeOut },
        };

  // Active state default: si no hay seccion activa (hero/top), destacar el primer item.
  const isActive = (href: string, index: number) =>
    activeId === href.slice(1) || (!activeId && index === 0);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="rsd-mobile-menu"
          id="rsd-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Menú de navegación"
          ref={panelRef}
          tabIndex={-1}
          initial={panel.initial}
          animate={panel.animate}
          exit={panel.exit}
          transition={{ duration: 0.52, ease: easeOut }}
          drag={reduceMotion ? false : "y"}
          dragControls={dragControls}
          dragListener={false}
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0, bottom: 0.4 }}
          onDragEnd={(_e, info) => {
            if (info.offset.y > 140 || info.velocity.y > 600) onClose();
          }}
        >
          <div className="rsd-mm-bg" aria-hidden="true" />
          <img className="rsd-mm-scene" src={storyCamperSceneSrc} alt="" aria-hidden="true" />

          {/* Grabber: handle real para iniciar el drag (swipe-down to close) */}
          <div
            className="rsd-mm-grabber"
            aria-hidden="true"
            onPointerDown={(e) => dragControls.start(e)}
          />

          <div className="rsd-mm-inner">
            <div className="rsd-mm-top">
              <span className="rsd-mm-brand">
                <img src={logoRuteandoSrc} alt="Ruteando Sin Destino" />
              </span>
              <button
                type="button"
                className="rsd-mm-close"
                onClick={onClose}
                aria-label="Cerrar menú"
              >
                <X size={22} />
              </button>
            </div>

            <nav className="rsd-mm-nav" aria-label="Secciones">
              {items.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  className={`rsd-mm-link${isActive(l.href, i) ? " is-active" : ""}`}
                  onClick={onNavigate}
                  {...row(i)}
                >
                  <span className="rsd-mm-index">{String(i + 1).padStart(2, "0")}</span>
                  <span className="rsd-mm-text">
                    <span className="rsd-mm-label">{l.label}</span>
                    <span className="rsd-mm-hint">{l.hint}</span>
                  </span>
                  <ArrowUpRight className="rsd-mm-arrow" size={22} />
                </motion.a>
              ))}
            </nav>

            <img
              className="rsd-mm-route-band"
              src={waysRouteBandSrc}
              alt=""
              aria-hidden="true"
              loading="lazy"
            />

            <motion.div className="rsd-mm-foot" {...row(items.length)}>
              <img
                className="rsd-mm-stamp"
                src={tagAlaskaSrc}
                alt=""
                aria-hidden="true"
                loading="lazy"
              />
              <a className="rsd-mm-cta" href={cta.href} onClick={onNavigate}>
                Hablemos de una colaboración
                <ArrowRight size={20} />
              </a>
              <div className="rsd-mm-social">
                <span className="rsd-mm-social-label">Seguinos</span>
                <div className="rsd-mm-social-icons">
                  {SOCIAL_LINKS.map((item) => (
                    <a
                      key={item.key}
                      className={`rsd-mm-social-icon rsd-footer-icon--${item.key}`}
                      href={item.href}
                      aria-label={item.aria}
                      onClick={onClose}
                      {...(item.ext ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    >
                      <img src={FOOTER_ICONS[item.key]} alt="" loading="lazy" />
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function RuteandoLanding() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeId, setActiveId] = useState("");
  // Al navegar desde el menu no restauramos el scroll viejo (dejamos ir al anchor).
  const skipScrollRestore = useRef(false);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const closeMenu = () => setMenuOpen(false);
  const navigateFromMenu = () => {
    skipScrollRestore.current = true;
    setMenuOpen(false);
  };

  // Al cerrar el menu, devuelve el foco al boton hamburguesa.
  useEffect(() => {
    if (menuOpen) return;
    // Pequeno delay para que el AnimatePresence haya desmontado el dialog.
    const t = setTimeout(() => {
      if (toggleRef.current && document.activeElement !== toggleRef.current) {
        // Solo restaurar si el foco esta en el body (no en otro control activo).
        if (document.activeElement === document.body) {
          toggleRef.current.focus({ preventScroll: true });
        }
      }
    }, 60);
    return () => clearTimeout(t);
  }, [menuOpen]);

  useEffect(() => {
    const scroller = document.scrollingElement || document.documentElement;
    let lastY = scroller.scrollTop;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const y = scroller.scrollTop;
        const dy = y - lastY;
        setScrolled(y > 24);
        // Oculta al bajar (pasado un umbral); reaparece al subir o cerca del tope.
        if (y > 140 && dy > 4) setHidden(true);
        else if (dy < -4 || y <= 140) setHidden(false);
        // Marca la seccion activa (ultima cuyo inicio quedo por encima del header).
        let current = "";
        for (const l of NAV_LINKS) {
          const el = document.getElementById(l.href.slice(1));
          if (el && el.getBoundingClientRect().top <= 140) current = l.href.slice(1);
        }
        setActiveId(current);
        lastY = y;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Bloquea el scroll de fondo mientras el menu full-screen esta abierto.
  // Tecnica position:fixed con offset: no clampa el scroll ni dispara eventos
  // (overflow:hidden forzaria scrollTop a 0 y cerraria el menu).
  useEffect(() => {
    if (!menuOpen) return;
    const scroller = document.scrollingElement || document.documentElement;
    const y = scroller.scrollTop;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${y}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      // Si se cerro navegando a un anchor, no pisamos ese scroll.
      if (skipScrollRestore.current) skipScrollRestore.current = false;
      else scroller.scrollTop = y;
    };
  }, [menuOpen]);

  // Cierra el menu con Escape o al volver a desktop.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    const mq = window.matchMedia("(min-width: 641px)");
    const onChange = () => {
      if (mq.matches) setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    mq.addEventListener("change", onChange);
    return () => {
      window.removeEventListener("keydown", onKey);
      mq.removeEventListener("change", onChange);
    };
  }, [menuOpen]);

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = description?.content;
    const theme = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousTheme = theme?.content;

    document.title = "Ruteando Sin Destino | Creadores de contenido de viaje en motorhome";
    if (description) {
      description.content =
        "Ruteando Sin Destino: Melisa y Diego viajan en motorhome por Sudamérica creando contenido real para marcas, destinos y experiencias.";
    }
    if (theme) theme.content = "#fff7e8";

    return () => {
      document.title = previousTitle;
      if (description && previousDescription) description.content = previousDescription;
      if (theme && previousTheme) theme.content = previousTheme;
    };
  }, []);

  return (
    <div className="rsd-page">
      <header
        className={`rsd-header${scrolled ? " is-scrolled" : ""}${hidden ? " is-hidden" : ""}${menuOpen ? " is-menu-open" : ""}`}
      >
        <a className="rsd-brand" href="#inicio" aria-label="Ruteando Sin Destino">
          <img src={logoRuteandoSrc} alt="" aria-hidden="true" />
        </a>
        <nav className="rsd-nav" aria-label="Navegación Ruteando Sin Destino">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href}>
              {l.label}
            </a>
          ))}
        </nav>
      </header>

      {/* Toggle fuera del header: permanece accesible cuando el header se auto-esconde */}
      <button
        ref={toggleRef}
        type="button"
        className={`rsd-nav-toggle${hidden ? " is-floating" : ""}${menuOpen ? " is-open" : ""}`}
        aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
        aria-expanded={menuOpen}
        aria-controls="rsd-menu"
        onClick={() => setMenuOpen((v) => !v)}
      >
        <span className="rsd-nav-toggle-bars" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      </button>

      <RsdMobileMenu
        open={menuOpen}
        onClose={closeMenu}
        onNavigate={navigateFromMenu}
        activeId={activeId}
      />

      <main id="inicio">
        <RsdHero />

        <RsdStorySection />

        <RsdMapaRutaSection />

        <section className="rsd-metrics" id="media-kit" aria-label="Métricas del proyecto">
          <div className="rsd-route" aria-hidden="true" />
          <div className="rsd-milestones">
            {rsdContent.metrics.map((metric, index) => (
              <RsdReveal className="rsd-milestone" delay={index * 0.09} key={metric.label}>
                <span className="rsd-milestone-stamp">
                  <img
                    src={METRIC_BADGES[metric.icon] ?? METRIC_BADGES.community}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                  />
                </span>
                <span className="rsd-milestone-text">
                  <RsdMetricValue value={metric.value} />
                  <span className="rsd-milestone-label">{metric.label}</span>
                </span>
              </RsdReveal>
            ))}
          </div>
        </section>

        <section className="rsd-section rsd-brand-fit" id="marcas">
          <SectionIntro
            eyebrow="Para qué marcas tiene sentido"
            eyebrowImg={tagMarcasSrc}
            title={
              <>
                Creamos contenido para marcas que encajan con una{" "}
                <span className="rsd-emph rsd-emph-real">vida en movimiento</span>.
              </>
            }
            text={rsdContent.brandFitLead}
          />
          <div className="rsd-card-grid rsd-brand-grid">
            {rsdContent.brandCards.map((item, index) => (
              <RsdReveal className="rsd-brand-card" delay={index * 0.035} key={item.title}>
                <img
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  src={BRAND_IMAGES[item.image] ?? BRAND_IMAGES.hospedajes}
                />
                <span className="rsd-brand-caption">
                  <strong>{item.title}</strong>
                  <em>{item.text}</em>
                </span>
              </RsdReveal>
            ))}
          </div>
        </section>

        <RsdPortfolio />

        <RsdContactCta />
      </main>

      <RsdFooter />
      <RsdWhatsappFab />
    </div>
  );
}

export default RuteandoLanding;
